import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, TrendingUp, ArrowUpRight, ArrowDownRight, Loader2, Lock, RefreshCcw, X, Pause, Play, Sparkles } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const riskColor = (r) => r === 'low' ? 'bg-green-100 text-green-700' : r === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';

export function InvestmentPlansPanel({ walletBalance = 0, onWalletChanged }) {
  const [plans, setPlans] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [sips, setSips] = useState([]);
  const [navHistories, setNavHistories] = useState({}); // planId -> [{date,nav}]
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [investDlg, setInvestDlg] = useState(null);
  const [amount, setAmount] = useState('');
  const [redeemDlg, setRedeemDlg] = useState(null);
  const [redeemUnits, setRedeemUnits] = useState('');
  const [sipDlg, setSipDlg] = useState(null); // { plan }
  const [sipAmount, setSipAmount] = useState('');
  const [sipFreq, setSipFreq] = useState('monthly');
  const [sipDay, setSipDay] = useState(5);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, hRes, sRes] = await Promise.all([
        api.get('/investment-plans'),
        api.get('/investment-plans/my-holdings'),
        api.get('/investment-plans/sips').catch(() => ({ data: [] })),
      ]);
      const list = pRes.data || [];
      setPlans(list);
      setHoldings(hRes.data || []);
      setSips(sRes.data || []);
      // Fetch NAV histories in parallel
      const histResults = await Promise.all(list.map(p => api.get(`/investment-plans/${p.id}/nav-history?days=30`).then(r => [p.id, r.data]).catch(() => [p.id, []])));
      const map = {};
      histResults.forEach(([id, hist]) => { map[id] = hist; });
      setNavHistories(map);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Refresh NAVs every 30s so live-tracked plans stay fresh
  useEffect(() => { const t = setInterval(fetchAll, 30000); return () => clearInterval(t); }, [fetchAll]);

  const openInvest = (plan, mode = 'invest', holding = null) => {
    setInvestDlg({ plan, mode, holding });
    setAmount('');
  };

  const submitInvest = async () => {
    if (!investDlg) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > walletBalance) { toast.error(`Insufficient balance (max ${money(walletBalance)})`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/investment-plans/${investDlg.plan.id}/subscribe`, { amount: amt });
      toast.success(`${investDlg.mode === 'topup' ? 'Topped up' : 'Invested'} ${money(amt)} — ${Number(data.units).toFixed(4)} units @ NAV $${Number(data.nav).toFixed(4)}`);
      setInvestDlg(null);
      await fetchAll();
      onWalletChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitRedeem = async (all = false) => {
    if (!redeemDlg) return;
    const units = all ? null : parseFloat(redeemUnits);
    if (!all && (!units || units <= 0)) { toast.error('Enter units to redeem'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/investment-plans/holdings/${redeemDlg.id}/redeem`,
        all ? { all: true } : { units });
      toast.success(`Redeemed ${Number(data.units_redeemed).toFixed(4)} units → ${money(data.proceeds)} (fee ${money(data.fee)})`);
      setRedeemDlg(null); setRedeemUnits('');
      await fetchAll();
      onWalletChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitSip = async () => {
    if (!sipDlg) return;
    const amt = parseFloat(sipAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid SIP amount'); return; }
    setBusy(true);
    try {
      await api.post('/investment-plans/sips', {
        plan_id: sipDlg.plan.id, amount: amt, frequency: sipFreq, day_of_month: Number(sipDay) || 1,
      });
      toast.success(`SIP created — $${amt} ${sipFreq} into ${sipDlg.plan.name}`);
      setSipDlg(null); setSipAmount(''); setSipFreq('monthly'); setSipDay(5);
      await fetchAll();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancelSip = async (id) => {
    if (!window.confirm('Cancel this SIP? No further auto-invests will happen.')) return;
    try { await api.delete(`/investment-plans/sips/${id}`); toast.success('SIP cancelled'); await fetchAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggleSip = async (sip) => {
    const isPaused = sip.status === 'paused';
    try {
      await api.post(`/investment-plans/sips/${sip.id}/${isPaused ? 'resume' : 'pause'}`);
      toast.success(isPaused ? 'SIP resumed' : 'SIP paused');
      await fetchAll();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // NAV zoom dialog
  const [navZoom, setNavZoom] = useState(null); // { plan }
  const [navZoomData, setNavZoomData] = useState([]);
  const [navRange, setNavRange] = useState(30);
  const [navLoading, setNavLoading] = useState(false);
  const openNavZoom = async (plan, range = 30) => {
    setNavZoom({ plan }); setNavRange(range); setNavLoading(true);
    try {
      const { data } = await api.get(`/investment-plans/${plan.id}/nav-history?days=${range}`);
      setNavZoomData(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setNavLoading(false); }
  };
  const switchRange = async (r) => {
    if (!navZoom) return;
    setNavRange(r); setNavLoading(true);
    try {
      const { data } = await api.get(`/investment-plans/${navZoom.plan.id}/nav-history?days=${r}`);
      setNavZoomData(data);
    } finally { setNavLoading(false); }
  };

  // Portfolio allocation pie
  const pieData = useMemo(() => holdings
    .filter(h => h.status === 'active' && h.current_value > 0)
    .map(h => ({ name: h.plan_meta?.name || h.plan_name, value: h.current_value, color: h.plan_meta?.color || '#2563EB' })),
  [holdings]);

  // Top-performing plan of last 30 days (from cached NAV histories)
  const topPlan = useMemo(() => {
    let best = null;
    for (const p of plans) {
      const hist = navHistories[p.id];
      if (!hist || hist.length < 2) continue;
      const first = hist[0].nav, last = hist[hist.length - 1].nav;
      if (!first) continue;
      const changePct = ((last - first) / first) * 100;
      if (!best || changePct > best.changePct) best = { plan: p, changePct, currentNav: last };
    }
    return best;
  }, [plans, navHistories]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>;

  // Total portfolio value across holdings
  const totalInvested = holdings.reduce((s, h) => s + (h.total_invested || 0), 0);
  const totalValue = holdings.reduce((s, h) => s + (h.current_value || 0), 0);
  const totalUnrealized = totalValue - totalInvested;

  return (
    <div className="space-y-6" data-testid="investment-plans-panel">
      {/* Hero: top-performing plan of last 30 days */}
      {topPlan && (
        <div
          className="rounded-lg p-5 relative overflow-hidden border cursor-pointer group"
          style={{ background: `linear-gradient(135deg, ${topPlan.plan.color}22 0%, ${topPlan.plan.color}05 60%, white 100%)`, borderColor: `${topPlan.plan.color}55` }}
          onClick={() => openInvest(topPlan.plan)}
          data-testid="plan-hero"
        >
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center text-4xl flex-shrink-0" style={{ background: `${topPlan.plan.color}22` }}>{topPlan.plan.icon_emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-black border-0 text-[10px] font-black flex items-center gap-1">
                  <Sparkles size={10} className="fill-black" /> TOP PERFORMER · 30D
                </Badge>
                <Badge className="text-[10px] border-0 bg-white text-brand-text-secondary capitalize">{topPlan.plan.category}</Badge>
              </div>
              <h3 className="font-heading text-xl font-bold text-brand-text-primary truncate">{topPlan.plan.name}</h3>
              <p className="text-xs text-brand-text-secondary line-clamp-1">{topPlan.plan.description}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">30-day return</p>
              <p className={`font-heading text-3xl font-black ${topPlan.changePct >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{topPlan.changePct >= 0 ? '+' : ''}{topPlan.changePct.toFixed(2)}%</p>
              <Button size="sm" className="mt-1 h-8 text-white shadow-md text-xs" style={{ background: topPlan.plan.color }} data-testid="plan-hero-invest">
                <TrendingUp size={12} className="mr-1" /> Invest Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio summary + wallet */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-brand-border bg-white"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold flex items-center gap-1"><Wallet size={11} /> Wallet</p>
          <p className="font-heading text-xl font-bold text-brand-text-primary mt-1">{money(walletBalance)}</p>
        </CardContent></Card>
        <Card className="border-brand-border bg-white"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Total Invested</p>
          <p className="font-heading text-xl font-bold text-brand-text-primary mt-1">{money(totalInvested)}</p>
        </CardContent></Card>
        <Card className="border-brand-border bg-white"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Current Value</p>
          <p className="font-heading text-xl font-bold text-brand-primary mt-1">{money(totalValue)}</p>
        </CardContent></Card>
        <Card className={`border ${totalUnrealized >= 0 ? 'border-brand-success/30 bg-brand-success/5' : 'border-brand-error/30 bg-brand-error/5'}`}><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Unrealized P&L</p>
          <p className={`font-heading text-xl font-bold mt-1 ${totalUnrealized >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{totalUnrealized >= 0 ? '+' : ''}{money(totalUnrealized)}</p>
        </CardContent></Card>
      </div>

      {/* Portfolio allocation pie + SIPs strip */}
      {(pieData.length > 0 || sips.filter(s => s.status === 'active' || s.status === 'paused').length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {pieData.length > 0 && (
            <Card className="border-brand-border bg-white lg:col-span-1" data-testid="portfolio-pie">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold mb-2">Portfolio Allocation</p>
                <div className="flex items-center gap-3">
                  <div style={{ width: 130, height: 130 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={60} paddingAngle={2}>
                          {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => money(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1 min-w-0">
                    {pieData.map(d => (
                      <div key={d.name} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                        <span className="text-brand-text-secondary truncate flex-1">{d.name}</span>
                        <span className="font-mono font-semibold">{((d.value / totalValue) * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {sips.filter(s => s.status === 'active' || s.status === 'paused').length > 0 && (
            <Card className="border-brand-border bg-white lg:col-span-2" data-testid="active-sips">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold mb-2 flex items-center gap-1"><RefreshCcw size={11} /> Auto-invest (SIP)</p>
                <div className="space-y-1.5">
                  {sips.filter(s => s.status === 'active' || s.status === 'paused').map(s => {
                    const isPaused = s.status === 'paused';
                    return (
                      <div key={s.id} className={`flex items-center gap-2 text-xs rounded p-2 ${isPaused ? 'bg-amber-50 border border-amber-200' : 'bg-brand-bg/50'}`} data-testid={`sip-row-${s.id}`}>
                        <span className="font-medium flex-1 truncate">{s.plan_name}</span>
                        <Badge className={`text-[9px] border-0 ${isPaused ? 'bg-amber-200 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>{isPaused ? 'paused' : s.frequency}</Badge>
                        <span className="font-mono font-bold">{money(s.amount)}</span>
                        {!isPaused && <span className="text-brand-text-secondary">next {String(s.next_run || '').slice(0, 10)}</span>}
                        <Button size="sm" variant="ghost" onClick={() => toggleSip(s)} className="h-6 px-1.5" data-testid={`sip-toggle-${s.id}`} title={isPaused ? 'Resume' : 'Pause'}>
                          {isPaused ? <Play size={11} className="text-brand-success" /> : <Pause size={11} className="text-amber-600" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => cancelSip(s.id)} className="h-6 text-red-600 hover:text-red-700 px-1.5" data-testid={`sip-cancel-${s.id}`} title="Cancel"><X size={11} /></Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* My holdings */}
      {holdings.length > 0 && (
        <div>
          <h3 className="font-heading text-sm font-bold text-brand-text-primary mb-2">My Holdings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {holdings.filter(h => h.status === 'active').map(h => (
              <Card key={h.id} className="border-brand-border bg-white overflow-hidden" data-testid={`holding-${h.id}`}>
                <div className="h-1" style={{ background: h.plan_meta?.color || '#2563EB' }} />
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ background: `${h.plan_meta?.color || '#2563EB'}18` }}>{h.plan_meta?.icon_emoji || '📈'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-brand-text-primary truncate">{h.plan_meta?.name || h.plan_name}</p>
                      <p className="text-[10px] text-brand-text-secondary">{Number(h.units).toFixed(6)} units · avg NAV ${Number(h.avg_nav || 0).toFixed(4)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-lg font-bold text-brand-text-primary">{money(h.current_value)}</p>
                      <p className={`text-xs font-semibold ${h.unrealized_pnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{h.unrealized_pnl >= 0 ? '+' : ''}{money(h.unrealized_pnl)} ({h.unrealized_pnl_pct >= 0 ? '+' : ''}{h.unrealized_pnl_pct}%)</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openInvest(plans.find(p => p.id === h.plan_id), 'topup', h)} className="flex-1 text-xs" data-testid={`topup-${h.id}`}><ArrowUpRight size={12} className="mr-1" /> Top-up</Button>
                    <Button size="sm" variant="outline" onClick={() => { setRedeemDlg(h); setRedeemUnits(''); }} className="flex-1 text-xs text-red-600 border-red-200" data-testid={`redeem-${h.id}`}><ArrowDownRight size={12} className="mr-1" /> Redeem</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Available plans grid */}
      <div>
        <h3 className="font-heading text-sm font-bold text-brand-text-primary mb-2">Available Plans</h3>
        {plans.length === 0 ? (
          <Card className="border-brand-border bg-white"><CardContent className="p-8 text-center text-sm text-brand-text-secondary">No plans available right now. Please check back later.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map(p => (
              <Card key={p.id} className="border-brand-border bg-white overflow-hidden hover:shadow-md transition-shadow" data-testid={`plan-${p.id}`}>
                <div className="h-1" style={{ background: p.color }} />
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center text-2xl" style={{ background: `${p.color}18` }}>{p.icon_emoji}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-brand-text-primary truncate">{p.name}</p>
                      <div className="flex gap-1 mt-0.5">
                        <Badge className={`text-[9px] border-0 ${riskColor(p.risk_level)}`}>{p.risk_level}</Badge>
                        <Badge className="text-[9px] border-0 bg-slate-100 text-slate-700 capitalize">{p.category}</Badge>
                        {p.lock_in_days > 0 && <Badge className="text-[9px] border-0 bg-amber-100 text-amber-700 flex items-center gap-0.5"><Lock size={7} /> {p.lock_in_days}d</Badge>}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-brand-text-secondary line-clamp-2 mb-2 h-8">{p.description || '—'}</p>
                  {/* NAV sparkline (30-day) — click to zoom */}
                  <NavSparkline data={navHistories[p.id]} color={p.color} onClick={() => openNavZoom(p, 30)} testid={`spark-${p.id}`} />
                  <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div className="bg-brand-bg/50 rounded p-2">
                      <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary font-semibold">{p.pricing_mode === 'live' ? 'Tracks' : 'Target'}</p>
                      <p className="font-mono font-bold text-brand-text-primary text-[11px]">{p.pricing_mode === 'live' ? p.live_symbol : `${p.static_apy_pct}% APY`}</p>
                    </div>
                    <div className="bg-brand-bg/50 rounded p-2">
                      <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary font-semibold">NAV</p>
                      <p className="font-mono font-bold text-brand-primary text-[11px]">${Number(p.current_nav).toFixed(4)}</p>
                    </div>
                    <div className="bg-brand-bg/50 rounded p-2">
                      <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary font-semibold">Min</p>
                      <p className="font-mono font-bold text-brand-text-primary text-[11px]">${p.min_investment}</p>
                    </div>
                    <div className="bg-brand-bg/50 rounded p-2">
                      <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary font-semibold">Fee</p>
                      <p className="font-mono font-bold text-brand-text-primary text-[11px]">{p.management_fee_pct}%</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button onClick={() => openInvest(p)} className="bg-brand-primary text-white text-xs h-9" data-testid={`invest-${p.id}`}>
                      <TrendingUp size={13} className="mr-1" /> Invest
                    </Button>
                    <Button variant="outline" onClick={() => { setSipDlg({ plan: p }); setSipAmount(String(p.min_investment || 100)); }} className="text-xs h-9" data-testid={`sip-${p.id}`}>
                      <RefreshCcw size={13} className="mr-1" /> Set SIP
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invest / Topup dialog */}
      <Dialog open={!!investDlg} onOpenChange={o => !o && setInvestDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="invest-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
            <span className="text-2xl">{investDlg?.plan?.icon_emoji}</span>
            {investDlg?.mode === 'topup' ? 'Top-up' : 'Invest in'} {investDlg?.plan?.name}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-brand-bg/40 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-brand-text-secondary">Current NAV</span><span className="font-mono font-bold">${Number(investDlg?.plan?.current_nav || 0).toFixed(4)}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-secondary">Wallet balance</span><span className="font-mono font-bold text-brand-success">{money(walletBalance)}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-secondary">Min / Max</span><span className="font-mono">${investDlg?.plan?.min_investment} / {investDlg?.plan?.max_investment ? `$${investDlg?.plan?.max_investment}` : '∞'}</span></div>
              {investDlg?.plan?.lock_in_days > 0 && <div className="flex justify-between"><span className="text-brand-text-secondary flex items-center gap-1"><Lock size={10} /> Lock-in</span><span>{investDlg?.plan?.lock_in_days} days</span></div>}
            </div>
            <div>
              <label className="text-xs text-brand-text-secondary block mb-1">Amount to invest ($)</label>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={String(investDlg?.plan?.min_investment || '100')} className="font-mono h-11 text-lg" data-testid="invest-amount" autoFocus />
              {amount && Number(amount) > 0 && investDlg?.plan?.current_nav > 0 && (
                <p className="text-xs text-brand-text-secondary mt-1.5">Est. units: <span className="font-mono font-bold text-brand-primary">{(Number(amount) / investDlg.plan.current_nav).toFixed(6)}</span></p>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[100, 500, 1000, 5000].map(v => (
                <Button key={v} size="sm" variant="outline" onClick={() => setAmount(String(v))} className="text-xs h-7">${v}</Button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setAmount(String(Math.floor(walletBalance)))} className="text-xs h-7">All</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvestDlg(null)}>Cancel</Button>
            <Button onClick={submitInvest} disabled={busy} className="bg-brand-primary text-white" data-testid="invest-confirm">
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem dialog */}
      <Dialog open={!!redeemDlg} onOpenChange={o => !o && setRedeemDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="redeem-dialog">
          <DialogHeader><DialogTitle className="font-heading">Redeem — {redeemDlg?.plan_meta?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-brand-bg/40 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-brand-text-secondary">Total units</span><span className="font-mono font-bold">{Number(redeemDlg?.units || 0).toFixed(6)}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-secondary">Current NAV</span><span className="font-mono font-bold">${Number(redeemDlg?.current_nav || 0).toFixed(4)}</span></div>
              <div className="flex justify-between"><span className="text-brand-text-secondary">Current value</span><span className="font-mono font-bold text-brand-primary">{money(redeemDlg?.current_value)}</span></div>
            </div>
            <div>
              <label className="text-xs text-brand-text-secondary block mb-1">Units to redeem</label>
              <Input type="number" value={redeemUnits} onChange={e => setRedeemUnits(e.target.value)} placeholder="0.000000" className="font-mono h-11" data-testid="redeem-units" />
              {redeemUnits && Number(redeemUnits) > 0 && (
                <p className="text-xs text-brand-text-secondary mt-1.5">Est. proceeds: <span className="font-mono font-bold text-brand-success">{money(Number(redeemUnits) * (redeemDlg?.current_nav || 0))}</span></p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemDlg(null)}>Cancel</Button>
            <Button onClick={() => submitRedeem(true)} disabled={busy} variant="outline" className="text-red-600 border-red-200" data-testid="redeem-all">Redeem All</Button>
            <Button onClick={() => submitRedeem(false)} disabled={busy} className="bg-brand-primary text-white" data-testid="redeem-partial">
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Redeem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* SIP dialog */}
      <Dialog open={!!sipDlg} onOpenChange={o => !o && setSipDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="sip-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
            <RefreshCcw size={16} className="text-brand-primary" /> Auto-invest into {sipDlg?.plan?.name}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-brand-text-secondary">A fixed amount will be auto-deducted from your wallet every {sipFreq === 'weekly' ? 'week' : 'month'} and invested into this plan.</p>
            <div>
              <label className="text-xs text-brand-text-secondary block mb-1">Amount per run ($)</label>
              <Input type="number" value={sipAmount} onChange={e => setSipAmount(e.target.value)} placeholder={String(sipDlg?.plan?.min_investment || '100')} className="font-mono h-10" data-testid="sip-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-brand-text-secondary block mb-1">Frequency</label>
                <Select value={sipFreq} onValueChange={setSipFreq}>
                  <SelectTrigger className="h-10 text-sm" data-testid="sip-freq"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
                </Select>
              </div>
              {sipFreq === 'monthly' && (
                <div>
                  <label className="text-xs text-brand-text-secondary block mb-1">Day of month</label>
                  <Input type="number" min="1" max="28" value={sipDay} onChange={e => setSipDay(e.target.value)} className="h-10 font-mono" data-testid="sip-day" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSipDlg(null)}>Cancel</Button>
            <Button onClick={submitSip} disabled={busy} className="bg-brand-primary text-white" data-testid="sip-confirm">
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Start SIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NAV zoom dialog */}
      <Dialog open={!!navZoom} onOpenChange={o => !o && setNavZoom(null)}>
        <DialogContent className="max-w-4xl" data-testid="nav-zoom-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
              <span className="text-xl">{navZoom?.plan?.icon_emoji}</span>
              <span>{navZoom?.plan?.name} — NAV History</span>
              {navZoomData.length > 1 && (() => {
                const first = navZoomData[0].nav, last = navZoomData[navZoomData.length - 1].nav;
                const ch = first ? ((last - first) / first) * 100 : 0;
                return <Badge className={`text-xs border-0 ml-auto ${ch >= 0 ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{ch >= 0 ? '+' : ''}{ch.toFixed(2)}% ({navRange}d)</Badge>;
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1.5">
              {[7, 30, 90, 365].map(r => (
                <Button key={r} size="sm" variant={navRange === r ? 'default' : 'outline'} onClick={() => switchRange(r)}
                  className={`text-xs h-7 ${navRange === r ? 'bg-brand-primary text-white' : ''}`}
                  data-testid={`nav-range-${r}`}>
                  {r === 365 ? '1Y' : `${r}D`}
                </Button>
              ))}
            </div>
            <div className="bg-brand-bg/40 rounded p-3" style={{ height: 340 }}>
              {navLoading ? (
                <div className="flex items-center justify-center h-full"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>
              ) : navZoomData.length < 2 ? (
                <div className="flex items-center justify-center h-full text-sm text-brand-text-secondary">No NAV history for this range yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={navZoomData.map(d => ({ date: String(d.date).slice(0, 10), nav: d.nav }))}>
                    <CartesianGrid stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={30} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `$${Number(v).toFixed(4)}`} />
                    <Line type="monotone" dataKey="nav" stroke={navZoom?.plan?.color || '#2563EB'} strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-brand-bg/40 rounded p-2"><p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Current NAV</p><p className="font-mono font-bold text-brand-primary">${Number(navZoom?.plan?.current_nav || 0).toFixed(4)}</p></div>
              <div className="bg-brand-bg/40 rounded p-2"><p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Category</p><p className="font-medium capitalize">{navZoom?.plan?.category}</p></div>
              <div className="bg-brand-bg/40 rounded p-2"><p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Mode</p><p className="font-medium">{navZoom?.plan?.pricing_mode === 'live' ? `Live · ${navZoom?.plan?.live_symbol}` : `Static · ${navZoom?.plan?.static_apy_pct}% APY`}</p></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNavZoom(null)}>Close</Button>
            <Button onClick={() => { const p = navZoom.plan; setNavZoom(null); openInvest(p); }} className="bg-brand-primary text-white" data-testid="nav-zoom-invest">
              <TrendingUp size={12} className="mr-1" /> Invest in this Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NavSparkline({ data, color = '#2563EB', onClick, testid }) {
  if (!data || data.length < 2) return <div className="h-10 bg-brand-bg/40 rounded flex items-center justify-center text-[10px] text-brand-text-secondary">NAV chart loading…</div>;
  const values = data.map(d => d.nav);
  const min = Math.min(...values), max = Math.max(...values), range = (max - min) || 1;
  const W = 200, H = 40, pad = 2;
  const stepX = (W - pad * 2) / (data.length - 1);
  const points = data.map((d, i) => `${pad + i * stepX},${H - pad - ((d.nav - min) / range) * (H - pad * 2)}`).join(' ');
  const last = data[data.length - 1].nav, first = data[0].nav;
  const changePct = first ? ((last - first) / first) * 100 : 0;
  const up = changePct >= 0;
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-2 bg-brand-bg/30 hover:bg-brand-bg/60 rounded p-1 w-full transition-colors" data-testid={testid || 'nav-sparkline'}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="flex-1">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
        <polyline fill={`${color}22`} stroke="none" points={`${pad},${H} ${points} ${W - pad},${H}`} />
      </svg>
      <span className={`text-[10px] font-mono font-bold ${up ? 'text-brand-success' : 'text-brand-error'}`}>{up ? '+' : ''}{changePct.toFixed(2)}%</span>
    </button>
  );
}
